import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { SiteRow } from './SiteRow';

const gbp = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const qty = (v) => Number(v).toLocaleString('en-GB', { maximumFractionDigits: 4 });

const Tile = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-slate-50 p-3">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-lg font-semibold text-slate-900" data-testid={testId}>{value}</div>
  </div>
);

const Exceptions = ({ items, testId }) => {
  if (!items.length) return null;
  return (
    <Alert className="border-amber-300 bg-amber-50" data-testid={testId}>
      <AlertTriangle className="h-4 w-4 text-amber-700" />
      <AlertDescription>
        <strong>{items.length} item{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} attention</strong>
        <details className="mt-1">
          <summary className="cursor-pointer text-sm text-amber-900">Show</summary>
          <ul className="mt-1 list-disc pl-5 text-sm">{items.map((x, i) => <li key={i}>{x.message}</li>)}</ul>
        </details>
      </AlertDescription>
    </Alert>
  );
};

const Table = ({ head, rows, testId }) => (
  <div className="max-h-[420px] overflow-auto rounded-md border" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white text-left text-xs uppercase text-slate-500">
        <tr>{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

export const SnapshotClosingStep = ({ closing, exceptions }) => (
  <div className="space-y-4" data-testid="snapshot-closing-step">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Tile label="Products" value={closing.line_count} testId="snapshot-closing-lines" />
      <Tile label="Total quantity" value={qty(closing.total_quantity)} testId="snapshot-closing-qty" />
      <Tile label="Closing value" value={gbp(closing.total_value)} testId="snapshot-closing-value" />
      <Tile label="Carried as opening" value={gbp(closing.carried_total_value_2dp)} testId="snapshot-carried-value" />
    </div>
    <Exceptions items={exceptions} testId="snapshot-closing-exceptions" />
    {closing.allowance_rows && (
      <p className="text-xs text-slate-600" data-testid="snapshot-allowance-rows-note">
        {closing.allowance_rows.codes.length} management-account row{closing.allowance_rows.codes.length === 1 ? '' : 's'} ({closing.allowance_rows.codes.join(', ')})
        {' '}routed to Management &gt; Allowances: not stock, closing value {gbp(closing.allowance_rows.closing_value)}, not carried.
      </p>
    )}
    <Table
      testId="snapshot-closing-table"
      head={closing.buckets?.length > 1 ? ['Product', 'Quantity', 'Value', 'By ledger'] : ['Product', 'Quantity', 'Value']}
      rows={closing.lines.map((l) => (
        <tr key={l.bp_id} className="border-t">
          <td className="px-3 py-1.5">{l.product_name}</td>
          <td className="px-3 py-1.5 tabular-nums" data-testid={`snapshot-closing-qty-${l.bp_id}`}>
            {qty(l.quantity)} {l.unit_label || 'btl'}
            {l.source_quantity && l.source_quantity !== l.quantity && (
              <span className="ml-1 text-xs text-slate-500">(source {qty(l.source_quantity)} case{l.quantity_proof?.[0]?.case_size ? ` of ${l.quantity_proof[0].case_size}` : ''})</span>
            )}
          </td>
          <td className="px-3 py-1.5 tabular-nums">{gbp(l.value)}</td>
          {closing.buckets?.length > 1 && (
            <td className="px-3 py-1.5 text-xs text-slate-600" data-testid={`snapshot-closing-buckets-${l.bp_id}`}>
              {(l.by_bucket || []).map((b) => `${bucketName(closing.buckets, b.code)}: ${qty(b.quantity)} ${b.unit_label} ${gbp(b.value)}`).join(' · ')}
            </td>
          )}
        </tr>
      ))}
    />
  </div>
);

const ROLE_LABELS = { location: 'Bar / location', cellar: 'Cellar', difference_ledger: 'Difference ledger (no stock)', inactive: 'Unused (no stock)' };
const bucketName = (buckets, code) => buckets.find((b) => b.code === code)?.name || code;

export const SnapshotSourceStep = ({ evidence, buckets }) => (
  <div className="space-y-4" data-testid="snapshot-source-step">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Tile label="Detected structure" value={evidence.structure?.label || '—'} testId="snapshot-structure-type" />
      <Tile label="Audit reference" value={evidence.audit_reference ?? '—'} testId="snapshot-audit-ref" />
      <Tile label="Audit period" value={`${evidence.open_date || '—'} → ${evidence.close_date || '—'}`} testId="snapshot-audit-period" />
      <Tile label="Closing value (source)" value={gbp(evidence.totals?.closing_value)} testId="snapshot-source-closing-value" />
    </div>
    <Table
      testId="snapshot-buckets-table"
      head={['Ledger', 'Role', 'Site location', 'Source rows', 'Closing qty', 'Closing value', 'Stock total in source', 'Carried value']}
      rows={buckets.map((b) => (
        <tr key={b.code} className="border-t" data-testid={`snapshot-bucket-${b.code}`}>
          <td className="px-3 py-1.5">{b.code} {b.name}</td>
          <td className="px-3 py-1.5">{ROLE_LABELS[b.role] || b.role}</td>
          <td className="px-3 py-1.5">{b.site_location_name || '—'}</td>
          <td className="px-3 py-1.5 tabular-nums">{b.rows}{b.blank_closing_rows ? ` (${b.blank_closing_rows} blank)` : ''}</td>
          <td className="px-3 py-1.5 tabular-nums">{qty(b.closing_quantity)}</td>
          <td className="px-3 py-1.5 tabular-nums">{gbp(b.closing_value)}</td>
          <td className="px-3 py-1.5 tabular-nums">{b.bar_stock_total === null ? '—' : gbp(b.bar_stock_total)}</td>
          <td className="px-3 py-1.5 tabular-nums">{gbp(b.carried_value)}</td>
        </tr>
      ))}
    />
  </div>
);

export const SnapshotYtdStep = ({ ytd, previousAudit, status }) => {
  if (status?.status === 'annual_reset') {
    return <Alert data-testid="snapshot-ytd-reset"><AlertDescription>
      Financial year end {status.fiscal_year_end}: {status.reason}
      {ytd ? ` The source's year to date (${ytd.window_start} to ${ytd.window_end}, takings ${gbp(ytd.actual_receipts_inc_vat)}) is kept here as evidence only and is not carried into the new year.` : ''}
    </AlertDescription></Alert>;
  }
  if (!ytd) {
    return <Alert data-testid="snapshot-ytd-missing"><AlertDescription>
      {status?.reason || 'The source has no year-to-date figures.'} Nothing is carried and nothing is estimated.
      {status?.fiscal_year_end ? ` Financial year end in the source: ${status.fiscal_year_end}.` : ''}
    </AlertDescription></Alert>;
  }
  return (
    <div className="space-y-4" data-testid="snapshot-ytd-step">
      <p className="text-sm text-slate-600" data-testid="snapshot-ytd-source">
        Year to date {ytd.window_start} to {ytd.window_end}, ending with the previous audit
        {previousAudit ? ` (${previousAudit.open_date} to ${previousAudit.close_date})` : ''}.
        {ytd.reconciles_to_audit_history ? ' These figures match the audit history in the source.' : ''}
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Tile label="Takings inc VAT" value={gbp(ytd.actual_receipts_inc_vat)} testId="snapshot-ytd-takings" />
        <Tile label="Result inc VAT" value={gbp(ytd.result_inc_vat)} testId="snapshot-ytd-result" />
        <Tile label="Gross profit" value={gbp(ytd.gross_profit)} testId="snapshot-ytd-gp" />
      </div>
      {!ytd.reconciles_to_audit_history && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="snapshot-ytd-unreconciled"><AlertDescription>These figures do not match the audit history in the source.</AlertDescription></Alert>
      )}
      <p className="text-xs text-slate-500" data-testid="snapshot-ytd-carry-note">
        {ytd.reconciles_to_audit_history
          ? "On publish, these figures are copied once into the next new stocktake's Previous YTD, labelled with this source and period."
          : 'Because these figures do not match the audit history, they will not be copied into the next stocktake.'}
        {' '}Estimated takings and management figures are not in the source and stay blank.
      </p>
    </div>
  );
};


const GAP_LINES = [
  ['no_measure', 'have a bar price but no matching measure'],
  ['no_price', 'have no bar price in the source'],
  ['price_conflict', 'have different prices across merged source codes'],
  ['zero_cost', 'have closing stock with no cost in the source (carried at £0)'],
  ['not_matched', 'source codes have no approved product (not carried)'],
  ['unit_exception', 'lines are carried in cases because bottles cannot be proven'],
  ['allowance_with_stock', 'allowance-routed row groups hold closing stock (not carried)'],
  ['blank_closing', 'difference-ledger groups have blank closing quantities (recorded as blank, nothing carried)'],
  ['ledger_stock', 'ledger rows hold closing stock outside a stock location (not carried)'],
];

export const GapReview = ({ gaps, acknowledged, onChange, disabled }) => {
  if (!gaps?.total) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="snapshot-gap-review">
      <div className="text-sm font-semibold text-amber-900">Before you publish</div>
      <ul className="list-disc pl-5 text-sm text-amber-900" data-testid="snapshot-gap-summary">
        {GAP_LINES.filter(([k]) => gaps[k]).map(([k, text]) => (
          <li key={k} data-testid={`snapshot-gap-${k}`}><strong>{gaps[k]}</strong> {text}</li>
        ))}
      </ul>
      <p className="text-xs text-amber-800">These stay blank. Nothing is estimated. Unpriced products need a measure and price before the next stocktake can value sales.</p>
      <label className="flex items-start gap-2 text-sm text-amber-950">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={acknowledged} disabled={disabled}
          onChange={(e) => onChange(e.target.checked)} data-testid="snapshot-gap-ack-checkbox" />
        I've reviewed these gaps and want to publish with them left blank.
      </label>
    </div>
  );
};

export const SnapshotSiteStep = ({ site, assignedNow, exceptions, conversionId, canEdit, onSaved }) => {
  const [onlyAffected, setOnlyAffected] = React.useState(true);
  const affected = site.lines.filter((l) => (l.source_status || l.price_status) !== 'priced');
  const rows = onlyAffected ? affected : site.lines;
  return (
    <div className="space-y-4" data-testid="snapshot-site-step">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Tile label="Approved product matches" value={site.approved_matches} testId="snapshot-site-matches" />
        <Tile label={assignedNow != null ? 'Assigned to this site now' : 'Assigned to this site'}
          value={`${assignedNow ?? site.assigned} / ${site.lines.length}`} testId="snapshot-site-assigned" />
        {assignedNow != null && assignedNow !== site.assigned && (
          <p className="text-xs text-slate-500 md:col-span-3" data-testid="snapshot-site-assigned-note">
            {site.assigned} were assigned when this snapshot was published; the rest were assigned when it was carried forward.
          </p>
        )}
        <Tile label="With measure and price" value={`${site.priced} / ${site.lines.length}`} testId="snapshot-site-priced" />
      </div>
      <Exceptions items={exceptions} testId="snapshot-site-exceptions" />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2" data-testid="snapshot-site-affected-filter">
          <input type="checkbox" className="h-4 w-4" checked={onlyAffected} onChange={(e) => setOnlyAffected(e.target.checked)} data-testid="snapshot-site-affected-checkbox" />
          Only products missing a measure or price in the source ({affected.length})
        </label>
        <span className="text-xs text-slate-500" data-testid="snapshot-site-help">{canEdit
          ? <>Entries are kept with the draft, marked "Entered by user", and reach the site's price list only when the published snapshot is carried into the next stocktake. Source prices are never overwritten.</>
          : 'Read only: measures and prices are shown as saved in this snapshot.'}</span>
      </div>
      <Table
        testId="snapshot-site-table"
        head={['Product', 'Measure', 'Price per measure (GBP)', '']}
        rows={rows.map((l) => <SiteRow key={`${l.bp_id}-${l.user_input?.entered_at || ''}`} l={l} conversionId={conversionId} canEdit={canEdit} onSaved={onSaved} />)}
      />
    </div>
  );
};
